import { useVirtualizer } from "@tanstack/react-virtual";
import { CSSProperties, ReactNode, useCallback, useLayoutEffect, useRef } from "react";

import Box from "ds/components/Box";
import { TreeNode } from "utils/tree";

import {
  TREE_LIST_OVERSCAN,
  TREE_LIST_ROW_HEIGHT_ESTIMATE,
  TREE_LIST_VIRTUALIZED_VIEWPORT,
} from "../constants";
import styles from "../styles.module.css";
import { BaseTreeListItemProps } from "../types";
import TreeListVirtualizedRow from "../VirtualizedRow";

type TreeListVirtualizedInnerStyle = CSSProperties & {
  "--tree-list-indent-levels": number;
};

type TreeListVirtualizedProps<T extends object> = {
  visibleNodes: TreeNode<T>[];
  renderItem: (item: BaseTreeListItemProps<T>) => ReactNode;
  renderEmptyItem?: () => ReactNode;
  isEmpty: boolean;
  selectedKey?: string;
  focusedKey?: string;
  treeDepth: number;
  maxDepth: number;
  id?: string;
  ariaLabel?: string;
};

const TreeListVirtualized = <T extends object>({
  visibleNodes,
  renderItem,
  renderEmptyItem,
  isEmpty,
  selectedKey,
  focusedKey,
  treeDepth,
  maxDepth,
  id,
  ariaLabel,
}: TreeListVirtualizedProps<T>) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const hasScrolledToSelected = useRef(false);

  const getItemKey = useCallback((index: number) => visibleNodes[index].id, [visibleNodes]);

  const virtualizer = useVirtualizer({
    count: visibleNodes.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => TREE_LIST_ROW_HEIGHT_ESTIMATE,
    overscan: TREE_LIST_OVERSCAN,
    getItemKey,
    initialRect: TREE_LIST_VIRTUALIZED_VIEWPORT,
  });

  const scrollToKey = useCallback(
    (key: string, align: "center" | "auto" = "auto") => {
      const index = visibleNodes.findIndex((node) => node.id === key);

      if (index >= 0) {
        virtualizer.scrollToIndex(index, { align });
      }
    },
    [virtualizer, visibleNodes]
  );

  useLayoutEffect(() => {
    if (!focusedKey) {
      return;
    }

    scrollToKey(focusedKey);
  }, [focusedKey, scrollToKey]);

  useLayoutEffect(() => {
    if (hasScrolledToSelected.current || !selectedKey) {
      return;
    }

    scrollToKey(selectedKey, "center");
    hasScrolledToSelected.current = true;
  }, [scrollToKey, selectedKey]);

  const innerStyle: TreeListVirtualizedInnerStyle = {
    height: virtualizer.getTotalSize(),
    "--tree-list-indent-levels": Math.max(treeDepth - 1, 0),
  };

  return (
    <Box
      ref={scrollRef}
      direction="column"
      role="tree"
      tabIndex={0}
      aria-label={ariaLabel}
      id={id}
      data-maxdepth={treeDepth > maxDepth ? treeDepth : undefined}
      className={styles.virtualizedScroll}
    >
      {isEmpty && renderEmptyItem?.()}
      {!isEmpty && (
        <Box relative shrink="0" className={styles.virtualizedInner} style={innerStyle}>
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const node = visibleNodes[virtualRow.index];

            if (!node) {
              return null;
            }

            return (
              <TreeListVirtualizedRow
                key={virtualRow.key}
                node={node}
                index={virtualRow.index}
                start={virtualRow.start}
                renderItem={renderItem}
                measureElement={(element) => {
                  virtualizer.measureElement(element);
                }}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};

TreeListVirtualized.displayName = "DS.TreeList.Virtualized";

export default TreeListVirtualized;
